import React from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "..";
import {
  setQueryResults,
  sendBoundsToStore,
  setQueryToStore,
  setLoadingStatus
} from "../store/reducers/map";
import { mapSelectors } from "../store/selectors";
import Partner from "../types/Partner";

export interface MapServiceProps {
  executeQuery?: (query: string) => Promise<Partner[]>;
  trackMyLocation?: () => Promise<Partner[]>;
  setBounds?: (partners: Partner[]) => void;
}

interface GeocodeRequest {
  address?: string;
  location?: Location;
}

interface Location {
  lat: number;
  lng: number;
}

const withMapService = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P & MapServiceProps> => props => {
  const partners = useSelector(mapSelectors.getPartners);
  const dispatch = useAppDispatch();

  const executeQuery = async (query: string) => {
    const location = await geocode(query);
    if (typeof location !== "string") {
      const closestPartners = getClosestPartners(location, 10);
      if (!closestPartners.length) {
        noResultsActions();
        return;
      }
      setBoundsAndDispatchQueryResults(closestPartners);
      dispatch(setLoadingStatus({ isLoading: false }));
      return closestPartners;
    }
  };

  const trackMyLocation = async () => {
    // #review
    const location = await getCurrentLocalization();
    // const location = { lat: 49.95022, lng: 19.26076 };

    const locationLatLngObj = createLatLngGoogleMapsObj(
      location.lat,
      location.lng
    );
    const formattedAddress = await geocode(location);
    const closestPartners = getClosestPartners(locationLatLngObj, 10);
    setBoundsAndDispatchQueryResults(closestPartners);
    if (typeof formattedAddress === "string") {
      dispatch(setQueryToStore({ query: formattedAddress }));
    }
    dispatch(setLoadingStatus({ isLoading: false }));
    return closestPartners;
  };

  const setBounds = (partners: Partner[]) => {
    const bounds = createBounds(partners);
    dispatch(sendBoundsToStore({ bounds }));
  };

  const createBounds = (partners: Partner[]): google.maps.LatLngBounds => {
    const bounds = new google.maps.LatLngBounds();
    partners.map(partner => {
      const partnerLatLng = createLatLngGoogleMapsObj(partner.lat, partner.lng);
      return bounds.extend(partnerLatLng);
    });
    return bounds;
  };

  const getClosestPartners = (location: google.maps.LatLng, limit: number) => {
    const partnersWithDistances = setDistancesFromStartingLocationToPartners(
      location,
      partners
    );
    const sortedPartnersByDist = partnersWithDistances.sort(sortByDist);
    const closestPartners = sortedPartnersByDist.slice(0, limit);

    return closestPartners;
  };

  const noResultsActions = () => {
    dispatch(setLoadingStatus({ isLoading: false }));
    dispatch(setQueryResults({ queryResults: undefined }));
    window.alert("Nie znaleziono żadnych partnerów w pobliżu.");
  };

  const setBoundsAndDispatchQueryResults = (closestPartners: Partner[]) => {
    setBounds(closestPartners);
    dispatch(setQueryResults({ queryResults: closestPartners }));
  };

  const geocode = (
    query: string | Location
  ): Promise<google.maps.LatLng | string> => {
    return new Promise((resolve, reject) => {
      const request = createGeocodeRequest(query);
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(request, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK) {
          reject(status);
          dispatch(setLoadingStatus({ isLoading: false }));
          window.alert("Błąd geokodera z powodu: " + status);
        }
        resolve(
          request.address
            ? results[0].geometry.location
            : results[0].formatted_address
        );
      });
    });
  };

  const createGeocodeRequest = (request: string | Location): GeocodeRequest => {
    if (typeof request === "string") {
      return { address: request };
    }
    return { location: request };
  };

  const getCurrentLocalization = (): Promise<Location> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            resolve(pos);
          },
          error => {
            dispatch(setLoadingStatus({ isLoading: false }));
            window.alert(
              "Nie udało się namierzyć Twojej lokalizacji, spróbuj ponownie później."
            );
            reject(error);
          }
        );
      }
    });
  };

  const setDistancesFromStartingLocationToPartners = (
    startingLocation: google.maps.LatLng,
    partners: Partner[]
  ) => {
    const partnersWithDistances: Partner[] = partners.map(partner => {
      const partnerLatLng = createLatLngGoogleMapsObj(partner.lat, partner.lng);

      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        startingLocation,
        partnerLatLng
      );

      return { ...partner, distance };
    });

    return partnersWithDistances;
  };

  const createLatLngGoogleMapsObj = (
    lat: string | number,
    lng: string | number
  ): google.maps.LatLng => {
    const latLng = new google.maps.LatLng({
      lat: Number(lat),
      lng: Number(lng)
    });
    return latLng;
  };

  const sortByDist = (a: Partner, b: Partner) => {
    return a.distance - b.distance;
  };

  const propsLocal: MapServiceProps = {
    executeQuery,
    setBounds,
    trackMyLocation
  };

  return <Component {...propsLocal} {...props} />;
};

export default withMapService;
